import { useQuery } from "react-query";
import { sureApiCall } from "src/api/axiosClient/sureApi";
import { zodParseWithDetailedLog } from "src/api/apiErrorLogs";
import { ScoreSchema } from "src/api/sure-api/score/useGetOneScore.ts";
import { z } from "zod";
import { useTranslation } from "react-i18next";

export const getAllScoreKey = "getAllScore" as const;
const MAX_SCORE_CATALOG_PAGES = 1_000;
const SCORE_CATALOG_ENDPOINT = "/api/scores";
const SCORE_CATALOG_PAGE_SIZE = 100;

const ScoreCatalogPageSchema = z.object({
    "hydra:member": z.array(z.unknown()),
    "hydra:view": z
        .object({
            "hydra:next": z.string().optional(),
        })
        .optional(),
});

const normalizeScoreCatalogNextLink = (nextLink: string): { page: number; url: string } => {
    const fallbackBaseUrl = "https://score-catalog.local";
    let endpointUrl: URL;
    let nextUrl: URL;

    try {
        const apiBaseUrl = new URL(sureApiCall.defaults.baseURL ?? "/", fallbackBaseUrl);
        endpointUrl = new URL(SCORE_CATALOG_ENDPOINT, apiBaseUrl);
        nextUrl = new URL(nextLink, endpointUrl);
    } catch {
        throw new Error("Score catalog pagination link is invalid");
    }

    if (nextUrl.origin !== endpointUrl.origin || nextUrl.pathname !== endpointUrl.pathname || nextUrl.hash !== "") {
        throw new Error("Score catalog pagination link changes collection scope");
    }

    const allowedParameters = new Set(["itemsPerPage", "page"]);
    const parameters = [...nextUrl.searchParams.keys()];
    const pageValues = nextUrl.searchParams.getAll("page");
    const pageSizeValues = nextUrl.searchParams.getAll("itemsPerPage");
    const page = Number(pageValues[0]);

    if (
        parameters.some((parameter) => !allowedParameters.has(parameter)) ||
        pageValues.length !== 1 ||
        !/^[1-9]\d*$/.test(pageValues[0] ?? "") ||
        !Number.isSafeInteger(page) ||
        pageSizeValues.length !== 1 ||
        pageSizeValues[0] !== SCORE_CATALOG_PAGE_SIZE.toString()
    ) {
        throw new Error("Score catalog pagination link changes collection parameters");
    }

    return {
        page,
        url: `${SCORE_CATALOG_ENDPOINT}?itemsPerPage=${SCORE_CATALOG_PAGE_SIZE}&page=${page}`,
    };
};

export const useGetAllScore = () => {
    const { i18n } = useTranslation();
    const locale = i18n.resolvedLanguage || "en";
    const query = useQuery({
        queryKey: [getAllScoreKey, locale],
        staleTime: 5 * 60 * 1000,
        queryFn: async () => {
            const allScores: unknown[] = [];
            let nextUrl: string | undefined =
                `${SCORE_CATALOG_ENDPOINT}?page=1&itemsPerPage=${SCORE_CATALOG_PAGE_SIZE}`;
            let pagesFetched = 0;
            const visitedUrls = new Set<string>();

            while (nextUrl !== undefined) {
                if (visitedUrls.has(nextUrl)) {
                    throw new Error("Cyclic Hydra pagination link received for /api/scores");
                }
                if (pagesFetched >= MAX_SCORE_CATALOG_PAGES) {
                    throw new Error("Hydra pagination exceeded safe page limit for /api/scores");
                }
                visitedUrls.add(nextUrl);

                const response: { data: unknown } = await sureApiCall.get(nextUrl, {
                    headers: { "Accept-Language": locale },
                });
                const pageData: z.infer<typeof ScoreCatalogPageSchema> = zodParseWithDetailedLog(
                    response.data,
                    ScoreCatalogPageSchema,
                    {
                        operation: "GET",
                        endpoint: nextUrl,
                    },
                );

                allScores.push(...pageData["hydra:member"]);
                pagesFetched += 1;
                const nextLink = pageData["hydra:view"]?.["hydra:next"];
                if (nextLink === undefined) {
                    nextUrl = undefined;
                    continue;
                }
                if (pageData["hydra:member"].length === 0) {
                    throw new Error("Score catalog pagination made no progress");
                }

                const normalizedNextLink = normalizeScoreCatalogNextLink(nextLink);
                if (visitedUrls.has(normalizedNextLink.url)) {
                    throw new Error("Cyclic Hydra pagination link received for /api/scores");
                }
                if (normalizedNextLink.page !== pagesFetched + 1) {
                    throw new Error("Score catalog pagination link changes collection parameters");
                }
                nextUrl = normalizedNextLink.url;
            }

            return allScores;
        },
        select: (scores) => {
            const data = zodParseWithDetailedLog(scores, ScoreSchema.array(), {
                operation: "GET",
                endpoint: "/api/scores",
            });
            return data;
        },
    });

    return {
        scores: query.data,
        ...query,
    };
};
